import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { t } from '../i18n';

function Stars({ value, onChange, readonly = false, label = '' }) {
  return (
    <div className={`library-review-stars${readonly ? ' is-readonly' : ''}`} aria-label={label}>
      {[1, 2, 3, 4, 5].map((rating) => (
        readonly ? (
          <span key={rating} className={rating <= Number(value || 0) ? 'is-filled' : ''} aria-hidden="true">
            <Star size={14} fill="currentColor" />
          </span>
        ) : (
          <button
            type="button"
            key={rating}
            className={rating <= Number(value || 0) ? 'is-filled' : ''}
            onClick={() => onChange?.(rating)}
            aria-label={t('reviews.rateStar', undefined, { rating })}
          >
            <Star size={24} fill="currentColor" aria-hidden="true" />
          </button>
        )
      ))}
    </div>
  );
}

function LibraryReviews({ loadReviews, saveReview, canReview, initialData = null }) {
  const [data, setData] = useState(initialData);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(!initialData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(initialData?.myReview?.rating || 0);
  const [comment, setComment] = useState(initialData?.myReview?.comment || '');

  const refresh = async (nextPage = page) => {
    setLoading(true);
    setError('');
    try {
      const result = await loadReviews(nextPage);
      setData(result);
      setRating(result?.myReview?.rating || 0);
      setComment(result?.myReview?.comment || '');
    } catch (requestError) {
      setError(requestError.message || t('reviews.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialData && page === 1) return;
    refresh(page);
    // loadReviews is intentionally supplied as an inline target-specific callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const submit = async (event) => {
    event.preventDefault();
    if (!rating || saving) return;
    setSaving(true);
    setError('');
    try {
      await saveReview({ rating, comment: comment.trim() });
      await refresh(1);
      setPage(1);
    } catch (requestError) {
      setError(requestError.message || t('reviews.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const summary = data?.summary || {};
  const totalPages = Math.max(1, Math.ceil(Number(data?.total || 0) / Number(data?.pageSize || 10)));

  return (
    <section className="library-reviews">
      <header className="library-reviews-heading">
        <div>
          <h2>{t('reviews.title')}</h2>
          <p>{Number(summary.averageRating || 0).toFixed(1)} · {summary.ratingCount || 0} {t('reviews.ratings')}</p>
        </div>
        <Stars value={summary.averageRating} readonly label={t('reviews.averageRating')} />
      </header>

      {canReview && (
        <form className="library-review-form" onSubmit={submit}>
          <strong>{data?.myReview ? t('reviews.editYours') : t('reviews.leaveYours')}</strong>
          <Stars value={rating} onChange={setRating} />
          <p className="library-review-notice">{t('reviews.notice')}</p>
          <div className="library-review-comment-row">
            <input
              type="text"
              maxLength={200}
              value={comment}
              onChange={(event) => setComment(event.target.value.replace(/[\r\n]/g, ' '))}
              placeholder={t('reviews.commentPlaceholder')}
              aria-label={t('reviews.commentLabel')}
            />
            <button type="submit" disabled={!rating || saving}>{saving ? t('reviews.saving') : t('reviews.save')}</button>
          </div>
          <small>{comment.length}/200</small>
        </form>
      )}

      {!canReview && <p className="library-review-locked">{t('reviews.importFirst')}</p>}
      {error && <p className="library-review-error" role="alert">{error}</p>}
      {loading ? <p className="library-review-state">{t('reviews.loading')}</p> : (
        <div className="library-review-list">
          {(data?.reviews || []).length === 0 && <p className="library-review-state">{t('reviews.empty')}</p>}
          {(data?.reviews || []).map((review) => (
            <article key={review.id} className="library-review-row">
              <Stars value={review.rating} readonly />
              {review.comment && <p>{review.comment}</p>}
              <time>{new Date(review.updatedAt || review.createdAt).toLocaleDateString()}</time>
            </article>
          ))}
        </div>
      )}
      {totalPages > 1 && (
        <div className="library-review-pagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>{t('reviews.previous')}</button>
          <span>{page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>{t('reviews.next')}</button>
        </div>
      )}
    </section>
  );
}

export { Stars };
export default LibraryReviews;
